/**
 * useLocalStorage — drop-in replacement for useState that persists to
 * window.localStorage. Survives page refresh; per-browser only (NOT per
 * account — for that, use the API).
 *
 * Used in Settings to prevent the "UI lies" problem where the user
 * configures preferences and they vanish on refresh. Real per-account
 * persistence requires backend endpoints; this is the next-best thing
 * until those land.
 *
 * Usage:
 *   const [tone, setTone] = useLocalStorage<string>('settings.aiTone', 'professional');
 *
 * Notes:
 *   - Keys are namespaced manually (caller passes the full key)
 *   - Falls back to default if JSON.parse fails (corrupt storage)
 *   - SSR-safe: returns default during server render
 */
import { useState, useEffect, useCallback } from 'react';

export function useLocalStorage<T>(key: string, defaultValue: T): [T, (v: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === 'undefined') return defaultValue;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw == null) return defaultValue;
      return JSON.parse(raw) as T;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Quota exceeded / disabled storage — silently drop
    }
  }, [key, value]);

  const set = useCallback((v: T | ((prev: T) => T)) => {
    setValue((prev) => (typeof v === 'function' ? (v as (p: T) => T)(prev) : v));
  }, []);

  return [value, set];
}
